/**
* @file Input component for duration
* @author Heino Lenting
*/
import * as tslib_1 from "tslib";
import { InputBase } from '../input-base/input-base';
import { Validator } from 'breeze-client';
import * as moment from 'moment';
import { LocalisationService } from '../../../core/localisation.service';
import createAutoCorrectedDateTimePipe from '../../pipes/create-datetime.pipe';
import { durationToText } from '../../../core/helpers/durationToText';
/**
 * Class for the duration input component
 */
var InputDurationComponent = /** @class */ (function (_super) {
    tslib_1.__extends(InputDurationComponent, _super);
    /**
     * Creates an instance of InputDurationComponent.
     * @param {LocalisationService} localisationService The localisation service is used for the pipe.
     * @memberOf InputDurationComponent
     */
    function InputDurationComponent(localisationService) {
        var _this = _super.call(this) || this;
        _this.localisationService = localisationService;
        _this.duration = null;
        _this.placeholderKey = 'APP.INPUTS.DURATION_PLACEHOLDER';
        return _this;
    }
    Object.defineProperty(InputDurationComponent.prototype, "mask", {
        /**
         * Gets the mask.
         * @memberOf InputDurationComponent
         */
        get: function () {
            return function (value) {
                if (!value || value.length <= 2) {
                    return LocalisationService.toMaskArray(['\\d', '\\d']);
                }
                var msk = [];
                var numberOfNumbers = 0;
                for (var i = 0; i < value.length; i++) {
                    if (value[i] >= '0' && value[i] <= '9') {
                        numberOfNumbers++;
                    }
                }
                if (numberOfNumbers <= 2) {
                    return LocalisationService.toMaskArray(['\\d', '\\d']);
                }
                for (var i = 0; i < numberOfNumbers - 2; i++) {
                    msk.push('\\d');
                }
                msk.push(':');
                msk.push('\\d');
                msk.push('\\d');
                return LocalisationService.toMaskArray(msk);
            };
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(InputDurationComponent.prototype, "pipe", {
        /**
         * Gets the pipe.
         * @memberOf InputDurationComponent
         */
        get: function () {
            return createAutoCorrectedDateTimePipe(this.localisationService.duration.correctionFormat, true);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(InputDurationComponent.prototype, "valueAsText", {
        /** Gets the value of the duration component in HH:mm format. */
        get: function () {
            return this.valueText;
        },
        /** Sets the value of the duration from a text in format HH:mm. */
        set: function (value) {
            if (this.valueText === value) {
                return;
            }
            this.valueText = value;
            this.duration = this.textToDuration(value);
            if (this.duration) {
                this.value = this.duration.isValid() ? this.duration.toISOString() : '';
            }
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Test if string is valid based on format 'h:mm', 'hh:mm', 'hhh:mm' etc.
     * But also hh, hh:, :m, h:m etc.
     * The latter to support a better input flow.
     * @returns Returns a RegExpMatchArray when valid otherwise a null.
     */
    InputDurationComponent.prototype.isValidFormat = function (text) {
        if (!text) {
            return null;
        }
        return text.match(/(^[0-9][0-9]*:[0-5][0-9]$)|(^:[0-5][0-9]$)|(^:[0-5]$)|(^[0-9]*$)|(^:$)|(^[0-9]*:$)|(^[0-9]*:[0-5]$)/);
    };
    Object.defineProperty(InputDurationComponent.prototype, "hours", {
        get: function () {
            return (this.duration) ? Math.floor(this.duration.asMinutes() / 60) : 0;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(InputDurationComponent.prototype, "minutes", {
        get: function () {
            return (this.duration) ? this.duration.asMinutes() % 60 : 0;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Is called when the user leaves the input and enforces the right display format (HH:mm).
     */
    InputDurationComponent.prototype.leaveInput = function () {
        var durationText = this.textToDuration(this.valueText);
        if (durationText === null) {
            // valueText is not valid so
            // we force a validation error by setting
            // the value with the valueText which not complying to the ISO 8601 standard.
            // This will stop committing the ChangeSet.
            // and we can restore the invalid valueText
            this.value = this.valueText;
        }
    };
    /**
     * Override of base method that sets the value.
     * Now extended for updating also the displayed value when needed.
     * @param val The new duration value.
     */
    InputDurationComponent.prototype.setValue = function (val) {
        _super.prototype.setValue.call(this, val);
        if (val !== 'ERROR') {
            var durationText = this.textToDuration(this.valueText);
            if (durationText && durationText.toISOString() === val) {
                return;
            }
            if (val) {
                if (val.length > 2 && (val[0] === 'P' || (val[0] === '-' && val[1] === 'P'))) {
                    // Assuming the ISO 8601 standard
                    var durationValue = moment.duration(val);
                    this.valueText = durationToText(durationValue);
                }
                else {
                    // Restoring the invalid valueText, see also leaveInput
                    this.valueText = val;
                }
            }
        }
    };
    /**
     * The default control validator for date time.
     */
    InputDurationComponent.prototype.defaultControlValidator = function () {
        var self = this;
        return function (c) {
            return self.isValidValue(c.value);
        };
    };
    /**
     * Validates a value
     * @param newValue The value to be validated
     * @returns {ValidationErrors} has errors if value is false
     * @memberOf InputDurationComponent
     */
    InputDurationComponent.prototype.isValidValue = function (newValue) {
        var _a;
        if (this.textToDuration(newValue) != null) {
            var errorKey = Validator.duration().name;
            return _a = {},
                _a[errorKey] = Validator.messageTemplates[errorKey],
                _a;
        }
        return null;
    };
    /**
     * Overrides the default validator when the control already has one.
     * Override this method if another behavior is needed.
     * @protected
     */
    InputDurationComponent.prototype.initializeValidator = function () {
        this.usingDefaultValidator = true;
        this.control.setValidators(this.defaultControlValidator());
    };
    /**
     * Converts valid text input to a duration object.
     * @param text The text to convert.
     * @returns The duration otherwise null.
     */
    InputDurationComponent.prototype.textToDuration = function (text) {
        if (text === '') {
            return moment.duration(NaN);
        }
        if (text) {
            text = text.split('_').join('');
            text = text.split('.').join('');
            text = text.split(',').join('');
        }
        var test = this.isValidFormat(text);
        if (test === null) {
            return null;
        }
        if (test[7]) {
            // match with ^[0-9]*:[0-5]$
            return moment.duration(text + '0');
        }
        if (test[6]) {
            // match with ^[0-9]*:$
            return moment.duration(text + '00');
        }
        if (test[5]) {
            // match with ^:$
            return moment.duration('0:00');
        }
        if (test[4]) {
            // match with ^[0-9]*$
            return moment.duration('0:0' + text);
        }
        if (test[3]) {
            // match with ^:[0-5]$
            return moment.duration('0' + text + '0');
        }
        if (test[2]) {
            // match with ^:[0-5][0-9]$
            return moment.duration('0' + text);
        }
        if (test[1]) {
            // match with ^[0-9][0-9]*):[0-5][0-9]$
            return moment.duration(text);
        }
        return null;
    };
    return InputDurationComponent;
}(InputBase));
export { InputDurationComponent };
